import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Sparkles } from "lucide-react";
import { Fireflies } from "@/components/effects/Fireflies";
import { API, LOGO_URL } from "@/config";
import { useAuth } from "@/Admin";
import { trackPurchase } from "@/utils/ga4";

const CreateAccountPrompt = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  if (user && user.role === "customer") {
    return (
      <div className="mt-6 p-4 rounded-xl border border-emerald-500/40 bg-emerald-500/5 text-sm text-emerald-200" data-testid="account-linked-note">
        Signed in as <strong>{user.email}</strong> — this booking is now saved to your account.
      </div>
    );
  }
  if (user && user.role) return null; // employee/admin — no prompt
  return (
    <div className="mt-6 p-5 rounded-xl border border-[#FFD166]/35 bg-[#FFD166]/8 text-left" data-testid="create-account-prompt">
      <p className="eyebrow text-[#FFD166]">Want to track your booking?</p>
      <p className="text-sm text-[#F6F1E4] mt-2">Create a free account and you'll be able to view, edit, or cancel this booking anytime — plus check refund eligibility instantly.</p>
      <button onClick={() => nav("/login?role=customer")} className="btn-ghost mt-4" data-testid="create-account-cta">Create my account</button>
    </div>
  );
};

export const BookingSuccess = () => {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [status, setStatus] = useState("checking");
  // Elements flow returns `payment_intent`; the legacy hosted-checkout flow
  // returns `session_id`. We support both so existing links still work.
  const sessionId = sp.get("session_id");
  const paymentIntent = sp.get("payment_intent");
  const reservationId = sp.get("reservation_id"); // non-PII transaction identifier
  const free = sp.get("free") === "1";
  const ga4PurchaseFiredRef = useRef(false);

  useEffect(() => {
    if (free) { setStatus("paid"); return; }
    if (!sessionId && !paymentIntent) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        if (paymentIntent) {
          const res = await axios.get(`${API}/checkout/payment-status/${paymentIntent}`);
          if (res.data.payment_status === "paid" || res.data.status === "succeeded") {
            setStatus("paid"); return;
          }
          if (res.data.status === "canceled") { setStatus("expired"); return; }
        } else if (sessionId) {
          const res = await axios.get(`${API}/checkout/status/${sessionId}`);
          if (res.data.payment_status === "paid") { setStatus("paid"); return; }
          if (res.data.status === "expired") { setStatus("expired"); return; }
        }
      } catch (e) { console.warn("checkout status poll failed:", e?.message); }
      attempts++;
      if (attempts < 8) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId, paymentIntent, free]);

  // GA4 purchase event — fires once when payment is confirmed.
  // Uses reservation_id as the transaction identifier (non-sensitive UUID).
  // The purchase module handles its own deduplication across rerenders and
  // Stripe redirect-back / back-forward navigation.
  useEffect(() => {
    if (status !== "paid" || ga4PurchaseFiredRef.current) return;
    ga4PurchaseFiredRef.current = true;
    const txId = reservationId || paymentIntent || sessionId;
    if (!txId) return;
    // Retrieve cart snapshot stored by CheckoutPage before payment submission
    let snap = {};
    try {
      const raw = sessionStorage.getItem("_egr_ga4_cart");
      if (raw) snap = JSON.parse(raw);
    } catch (_) {}
    trackPurchase({
      transactionId: txId,
      value: snap.total || 0,
      items: snap.items || [],
      coupon: snap.coupon || undefined,
      affiliationCode: snap.affiliationCode || undefined,
    });
  }, [status, reservationId, paymentIntent, sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <Fireflies count={30} />
      <div className="glass p-12 max-w-xl text-center relative z-10">
        <img src={LOGO_URL} alt="" className="h-16 mx-auto mb-6" style={{ filter: "url(#knockoutBlack) drop-shadow(0 0 12px rgba(255,209,102,0.4))" }} />
        {status === "checking" && <p className="font-serif text-3xl">Summoning fireflies to confirm your booking…</p>}
        {status === "paid" && (
          <>
            <Sparkles className="w-10 h-10 mx-auto text-[#FFD166] mb-4" />
            <h1 className="font-serif text-5xl glow-text">Your magic is booked!</h1>
            <p className="mt-4 text-[#B9C8D5]">A confirmation email is drifting to you now. We'll email you the exact delivery details for your resort — either a meet-up time or a bell-services drop-off — within your chosen window.</p>
            <CreateAccountPrompt />
          </>
        )}
        {status === "expired" && <p className="font-serif text-3xl text-[#FFD166]">Session expired — please try again.</p>}
        {status === "timeout" && <p className="font-serif text-2xl">Still processing… check your email in a few minutes.</p>}
        {status === "error" && <p className="font-serif text-3xl">Something drifted off. Please contact us.</p>}
        <p className="mt-6 text-[11px] text-[#B9C8D5] max-w-md mx-auto leading-relaxed" data-testid="acceptance-statement">
          By completing this reservation, you accepted the Enchanted Gear Rentals{" "}
          <a href="/terms" target="_blank" rel="noreferrer" className="text-[#FFD166] underline" data-testid="acceptance-terms-link">Terms of Service</a>{" "}
          and any safety acknowledgments applicable to the equipment in your reservation.
        </p>
        <button className="btn-primary mt-8" onClick={() => nav("/")} data-testid="return-home">Return to the Bayou</button>
      </div>
    </div>
  );
};
